import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { GroupBase } from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { useFetchData } from "../../hooks/useFetch";
import { BasePaginateResponse } from "../../models/response/base-response";
import { ApiConfig } from "../../services/api-config";

// Định nghĩa interface cho các option trong dropdown
interface OptionType {
  id: string | number; // ID duy nhất của option
  name?: string; // Tên (tuỳ chọn)
  code?: string; // Mã (tuỳ chọn)
  username?: string; // Tên người dùng (tuỳ chọn)
  labelOption?: string; // Nhãn hiển thị trong dropdown
  isSelectAll?: boolean; // Đánh dấu option "Chọn tất cả"
  [key: string]: any; // Cho phép thêm các thuộc tính khác linh hoạt
}

// Định nghĩa interface cho props của component
interface SelectDropdownAsyncPaginateProps<T extends OptionType> {
  url: string; // URL API để lấy dữ liệu
  limit?: number; // Giới hạn số lượng item mỗi trang
  placeholder?: string; // Văn bản hiển thị khi chưa chọn
  onChange: (selected: T[]) => void; // Callback khi giá trị thay đổi
  defaultOptions?: T[]; // Danh sách option mặc định
  classNames?: string; // Class CSS để tùy chỉnh giao diện
  isMulti?: boolean; // Cho phép chọn nhiều option
  allOptions?: T; // Option "Tất cả" (nếu có)
  labelOption?: string; // Trường dùng làm nhãn hiển thị
  params?: Record<string, any>; // Tham số bổ sung cho API
  disabled?: boolean; // Trạng thái vô hiệu hóa dropdown
  projectId?: string; // ID dự án (nếu có)
  isFetching?: boolean; // Trạng thái tạo dữ liệu
  useFirstOptionAsDefault?: boolean; // Nếu true, sẽ chọn option đầu tiên làm mặc định
  enableSelectAll?: boolean; // Cho phép tính năng "Chọn tất cả"
  selectAllLabel?: string; // Nhãn cho option "Chọn tất cả"
  allowDeselectOnSingleClick?: boolean; // Cho phép nhấn lại item đã chọn để bỏ chọn (chỉ áp dụng khi isMulti = false)
  hideSelectedValue?: boolean; // Ẩn giá trị đã chọn khỏi trường hiển thị
  closeMenuOnSelectOverride?: boolean; // Ghi đè hành vi đóng menu sau khi chọn
  pendingCountMap?: Record<string | number, number>; // Map id -> số đơn đang treo, hiển thị kèm nhãn option
}

// Component generic nhận type T extends OptionType
const SelectDropdownAsyncPaginate = <T extends OptionType>({
  url,
  limit = 100,
  placeholder,
  onChange,
  defaultOptions = [],
  classNames = "w-[500px]",
  isMulti = false,
  allOptions,
  labelOption = "name",
  params = {},
  disabled = false,
  projectId = ApiConfig.PROJECT_ID.USER_SERVICE,
  isFetching = true,
  useFirstOptionAsDefault = false,
  enableSelectAll = false,
  selectAllLabel,
  allowDeselectOnSingleClick = false,
  hideSelectedValue = false,
  closeMenuOnSelectOverride,
  pendingCountMap,
}: SelectDropdownAsyncPaginateProps<T>) => {
  const { t } = useTranslation();
  const {
    request: fetchData,
    loading: apiLoading,
  } = useFetchData.Get<BasePaginateResponse<T[]>>({
    projectId: projectId,
  });
  const { request: fetchDefaultItems, baseResponse: defaultItemsResponse } =
    useFetchData.Get<BasePaginateResponse<T[]>>({
      projectId: projectId,
    });
  const mapOptions = (items: T[]): T[] =>
    items.map((item) => ({
      ...item,
      labelOption: String(
        item[labelOption as keyof T] ||
          item.name ||
          item.username ||
          t("common.no_label"),
      ),
    }));

  // Tạo option "Chọn tất cả" với id = -1
  // const createSelectAllOption = (): T => ({
  //   id: -1,
  //   name: selectAllLabel || t("common.select_all"),
  //   labelOption: selectAllLabel || t("common.select_all"),
  //   isSelectAll: true,
  // } as T);
  // Tạo option "Chọn tất cả" với id = -1, không set labelOption tĩnh
  const createSelectAllOption = useCallback(
    (): T =>
      ({
        id: -1,
        isSelectAll: true, // không set labelOption tĩnh
      }) as T,
    [],
  );
  const initialSelectedOptions =
    defaultOptions.length > 0
      ? mapOptions(defaultOptions)
      : allOptions
        ? [mapOptions([allOptions])[0]]
        : [];
  const initialOptions = allOptions ? [mapOptions([allOptions])[0]] : [];

  const [options, setOptions] = useState<T[]>(initialOptions);
  const [selectedOptions, setSelectedOptions] = useState<T[]>(
    initialSelectedOptions,
  );
  const [hasLoadedDefaultItems, setHasLoadedDefaultItems] =
    useState<boolean>(false);

  // Kiểm tra defaultOptions có đủ thông tin label để hiển thị mà không cần fetch bổ sung
  const areDefaultsComplete = defaultOptions.every((opt) => {
    const label =
      (opt as any)[labelOption as keyof T] ||
      opt.name ||
      opt.username ||
      opt.labelOption;
    return !!label;
  });

  const cache = useRef<Map<string, { data: T[]; hasMore: boolean }>>(new Map());
  const lastSearch = useRef<string>("");

  useEffect(() => {
    if (defaultOptions.length > 0) {
      setSelectedOptions(mapOptions(defaultOptions));
      setOptions((prev) => {
        const mappedDefaults = mapOptions(defaultOptions);
        return [
          ...mappedDefaults,
          ...prev.filter(
            (opt) => !defaultOptions.some((defOpt) => defOpt.id === opt.id),
          ),
        ];
      });
    }
  }, [defaultOptions]);

  // Fetch default items nếu cần
  useEffect(() => {
    if (!isFetching) return;
    // Chỉ fetch defaultItems khi defaultOptions chưa đủ thông tin để hiển thị
    if (
      defaultOptions.length > 0 &&
      !hasLoadedDefaultItems &&
      !allOptions &&
      !areDefaultsComplete
    ) {
      const missingOptionIds = defaultOptions
        .filter((option) => !options.some((o) => o.id === option.id))
        .map((option) => option.id);

      if (missingOptionIds.length > 0) {
        fetchDefaultItems({
          endPoint: url,
          data: { ids: missingOptionIds.join(","), ...params },
        });
        setHasLoadedDefaultItems(true);
      }
    }
  }, [
    defaultOptions,
    hasLoadedDefaultItems,
    url,
    params,
    fetchDefaultItems,
    options,
    allOptions,
    isFetching,
    areDefaultsComplete,
  ]);

  /**
   * Ghi kết quả 1 trang (search+page) vào cache + `options` hiển thị.
   *
   * TRƯỚC ĐÂY hàm này chạy trong 1 `useEffect` phản ứng theo `baseResponse` (state DÙNG CHUNG của hook
   * `useFetchData.Get` cho MỌI lần gọi `fetchData`, không phải state riêng từng request), gắn nhãn
   * search/page cho response bằng cách ĐOÁN qua `currentRequestId.current` — con trỏ này bị request MỚI
   * NHẤT ghi đè ngay khi gọi, không đợi request cũ trả lời. Nếu request cũ (vd tải trang mặc định lúc
   * mount, search rỗng) trả lời CHẬM hơn và về SAU khi người dùng đã gõ tìm kiếm mới, effect vẫn tưởng
   * đó là kết quả của tìm kiếm mới → cache sai + hiện nhầm danh sách không khớp từ khoá đang gõ.
   *
   * Sửa: `loadOptions` bên dưới `await` thẳng promise trả về từ `fetchData(...)` (hook đã trả kết quả
   * đúng của CHÍNH lần gọi đó, không qua state dùng chung) rồi gọi hàm này trực tiếp với dữ liệu chắc
   * chắn đúng — không còn phải đoán qua ref nào nữa.
   */
  const applyPageResult = useCallback(
    (search: string, page: number, list: T[], total_record: number) => {
      const hasMore = total_record > page * limit;
      const mappedList = mapOptions(list);
      const cacheKey = `${search}-${page}-${JSON.stringify(params)}`;

      cache.current.set(cacheKey, { data: mappedList, hasMore });

      setOptions((prev) => {
        const baseOptions = allOptions ? [mapOptions([allOptions])[0]] : [];

        // Kiểm tra xem có đang chọn "Tất cả" không (cho single select)
        const isSelectAllChosen =
          !isMulti && selectedOptions.some((option) => option.id === -1);
        const selectAllOption =
          enableSelectAll && !isSelectAllChosen ? [createSelectAllOption()] : [];

        if (page === 1) {
          return [...baseOptions, ...selectAllOption, ...mappedList];
        }
        const existingOptions = prev.filter(
          (opt) =>
            (!allOptions || opt.id !== allOptions.id) &&
            (!enableSelectAll || !opt.isSelectAll),
        );
        const uniqueNewOptions = mappedList.filter(
          (newOption) => !existingOptions.some((opt) => opt.id === newOption.id),
        );
        return [
          ...baseOptions,
          ...selectAllOption,
          ...existingOptions,
          ...uniqueNewOptions,
        ];
      });

      return { mappedList, hasMore };
    },
    [
      limit,
      allOptions,
      params,
      enableSelectAll,
      selectAllLabel || t("common.select_all"),
      selectedOptions,
      isMulti,
    ],
  );

  // Xử lý response từ defaultItems
  useEffect(() => {
    if (!defaultItemsResponse?.data?.list?.length) return;
    const fetchedDefaultItems = mapOptions(defaultItemsResponse.data.list);

    setOptions((prev) => {
      const baseOptions = allOptions ? [prev[0]] : [];

      // Kiểm tra xem có đang chọn "Tất cả" không (cho single select)
      const isSelectAllChosen =
        !isMulti && selectedOptions.some((option) => option.id === -1);
      const selectAllOption =
        enableSelectAll && !isSelectAllChosen ? [createSelectAllOption()] : [];

      const combined = [
        ...baseOptions,
        ...selectAllOption,
        ...fetchedDefaultItems,
      ];
      return combined.filter(
        (opt, idx, self) => idx === self.findIndex((o) => o.id === opt.id),
      );
    });
  }, [
    defaultItemsResponse,
    allOptions,
    enableSelectAll,
    selectAllLabel || t("common.select_all"),
    selectedOptions,
    isMulti,
  ]);

  useEffect(() => {
    if (
      useFirstOptionAsDefault &&
      !defaultOptions.length &&
      options.length > 0 &&
      selectedOptions.length === 0
    ) {
      // Ưu tiên chọn option "Tất cả" nếu có
      const selectAllOption = options.find(
        (opt) => opt.isSelectAll || opt.id === -1,
      );
      const firstOption =
        selectAllOption ||
        options.find((opt) => !opt.isSelectAll) ||
        options[0];

      if (firstOption && (!firstOption.isSelectAll || enableSelectAll)) {
        setSelectedOptions([firstOption]);
        onChange([firstOption]);
      }
    }
  }, [
    useFirstOptionAsDefault,
    defaultOptions,
    options,
    selectedOptions.length,
    onChange,
    enableSelectAll,
  ]);

  const loadOptions: LoadOptions<
    T,
    GroupBase<T>,
    { page: number }
  > = useCallback(
    async (search, _, additional) => {
      const page = search !== lastSearch.current ? 1 : additional?.page || 1;
      lastSearch.current = search;

      const cacheKey = `${search}-${page}-${JSON.stringify(params)}`;
      const cached = cache.current.get(cacheKey);
      if (cached) {
        const baseOptions = allOptions ? [mapOptions([allOptions])[0]] : [];

        // Kiểm tra xem có đang chọn "Tất cả" không (cho single select)
        const isSelectAllChosen =
          !isMulti && selectedOptions.some((option) => option.id === -1);
        const selectAllOption =
          enableSelectAll && page === 1 && !isSelectAllChosen
            ? [createSelectAllOption()]
            : [];

        return {
          options: [...baseOptions, ...selectAllOption, ...cached.data],
          hasMore: cached.hasMore,
          additional: { page: page + 1 },
        };
      }

      // `await` THẲNG kết quả của CHÍNH lần gọi này (hook trả kết quả trực tiếp, không qua state dùng
      // chung) — không còn cửa sổ để 1 response chậm của lần gọi trước ghi đè nhầm lên lần gọi này (xem
      // ghi chú ở `applyPageResult`). Race cũ: gõ tìm kiếm nhanh trong lúc trang vẫn đang tải mặc định
      // lúc mount → danh sách hiện ra không khớp từ khoá đang gõ.
      const res = await fetchData({
        endPoint: url,
        data: { key_search: search, page, limit, ...params },
      });
      const list = (res?.data as any)?.list ?? [];
      const total_record = (res?.data as any)?.total_record ?? 0;
      const { mappedList, hasMore } = applyPageResult(search, page, list, total_record);

      const baseOptions = allOptions ? [mapOptions([allOptions])[0]] : [];
      const isSelectAllChosen =
        !isMulti && selectedOptions.some((option) => option.id === -1);
      const selectAllOption =
        enableSelectAll && page === 1 && !isSelectAllChosen
          ? [createSelectAllOption()]
          : [];

      return {
        options: [...baseOptions, ...selectAllOption, ...mappedList],
        hasMore,
        additional: { page: page + 1 },
      };
    },
    [
      url,
      limit,
      fetchData,
      params,
      allOptions,
      enableSelectAll,
      selectAllLabel || t("common.select_all"),
      selectedOptions,
      isMulti,
      applyPageResult,
    ],
  );

  const customStyles = {
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
      color: "#7D7E81",
      fontSize: "14px",
      fontWeight: 600,
    }),
    placeholder: (base: any) => ({
      ...base,
      color: "#7D7E81",
      fontSize: "14px",
      fontWeight: 600,
    }),
    singleValue: (base: any) => ({
      ...base,
      color: "#7D7E81",
      fontSize: "14px",
      fontWeight: 600,
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: "#e2e8f0",
      borderRadius: "4px",
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      color: "#7D7E81",
      fontWeight: "bold",
    }),
    multiValueRemove: (base: any) => ({
      ...base,
      color: "#64748b",
      ":hover": { backgroundColor: "#cbd5e1", color: "#7D7E81" },
    }),
    option: (base: any, { data }: { data: T }) => ({
      ...base,
      fontWeight: data.isSelectAll ? "bold" : "normal",
      borderBottom: data.isSelectAll ? "1px solid #e2e8f0" : "none",
    }),
  };

  const getOptionLabel = (option: T) => {
    if (option.isSelectAll) {
      return selectAllLabel || t("common.select_all");
    }
    const name =
      option.labelOption ||
      option.username ||
      option.name ||
      t("common.no_label");
    const baseLabel = option.code ? `${name} (${option.code})` : name;
    // Hiển thị số đơn đang treo (chỉ khi tài xế có đơn treo) để người gán cân nhắc
    if (pendingCountMap && option.id != null) {
      const count = pendingCountMap[option.id];
      if (typeof count === "number" && count > 0) {
        return `${baseLabel} · ${count} đơn treo`;
      }
    }
    return baseLabel;
  };

  const getOptionValue = (option: T) => {
    if (option.isSelectAll) return "-1";
    return option.id?.toString() ?? "";
  };

  const handleChange = (newValue: any) => {
    if (!isMulti) {
      const newOption = (newValue as T) || null;

      if (
        allowDeselectOnSingleClick &&
        newOption &&
        selectedOptions[0] &&
        selectedOptions[0].id === newOption.id
      ) {
        setSelectedOptions([]);
        onChange([] as T[]);
        return;
      }

      const selectedValues = newOption ? [newOption] : [];
      setSelectedOptions(selectedValues);
      onChange(selectedValues);
      return;
    }

    // Xử lý cho multi select - giữ nguyên logic cũ
    const newValues = (newValue as T[]) || [];
    const selectAllOption = newValues.find(
      (option) => option.isSelectAll || option.id === -1,
    );
    const previouslyHadSelectAll = selectedOptions.some(
      (option) => option.id === -1,
    );

    if (selectAllOption && !previouslyHadSelectAll) {
      // Nếu vừa chọn "Chọn tất cả" - chỉ giữ lại option "Chọn tất cả"
      const selectAllResult = createSelectAllOption();
      setSelectedOptions([selectAllResult]);
      onChange([selectAllResult]);

      // Xóa tất cả options để dropdown trống
      setOptions([]);
    } else if (!selectAllOption && previouslyHadSelectAll) {
      // Nếu đang bỏ chọn "Chọn tất cả" - khôi phục lại các options
      const regularNewValues = newValues.filter(
        (option) =>
          !option.isSelectAll &&
          option.id !== -1 &&
          (!allOptions || option.id !== allOptions.id),
      );
      setSelectedOptions(regularNewValues);
      onChange(regularNewValues);

      // Khôi phục lại options từ cache
      const cacheKey = `${lastSearch.current}-1-${JSON.stringify(params)}`;
      const cachedData = cache.current.get(cacheKey);

      if (cachedData) {
        setOptions((prev) => {
          const baseOptions = allOptions ? [mapOptions([allOptions])[0]] : [];
          const selectAllOption = enableSelectAll
            ? [createSelectAllOption()]
            : [];
          const currentOptions = [
            ...baseOptions.filter(Boolean),
            ...selectAllOption,
            ...cachedData.data,
          ];
          return currentOptions;
        });
      } else {
        // Nếu không có cache, khôi phục với options cơ bản
        const baseOptions = allOptions ? [mapOptions([allOptions])[0]] : [];
        const selectAllOptionArray = enableSelectAll
          ? [createSelectAllOption()]
          : [];
        setOptions([...baseOptions.filter(Boolean), ...selectAllOptionArray]);
      }
    } else if (!selectAllOption) {
      // Chọn/bỏ chọn các options thường (khi chưa có "Chọn tất cả")
      const regularNewValues = newValues.filter(
        (option) =>
          !option.isSelectAll &&
          option.id !== -1 &&
          (!allOptions || option.id !== allOptions.id),
      );
      setSelectedOptions(regularNewValues);
      onChange(regularNewValues);
    }
  };

  // Kiểm tra xem có đang chọn "Chọn tất cả" không (chỉ áp dụng cho multi select)
  const hasSelectAllSelected =
    isMulti && selectedOptions.some((option) => option.id === -1);

  // Giá trị hiển thị trong dropdown
  const calculatedDisplayValue = (() => {
    if (!isMulti) {
      return selectedOptions[0] || null;
    }

    // Nếu có chọn "Chọn tất cả", chỉ hiển thị option đó
    const hasSelectAll = selectedOptions.some((option) => option.id === -1);
    if (hasSelectAll) {
      return selectedOptions.filter((option) => option.id === -1);
    }

    return selectedOptions;
  })();

  const displayValue = hideSelectedValue
    ? isMulti
      ? []
      : null
    : calculatedDisplayValue;
  const closeMenuOnSelectValue =
    typeof closeMenuOnSelectOverride === "boolean"
      ? closeMenuOnSelectOverride
      : !isMulti || hasSelectAllSelected;

  return (
    <AsyncPaginate
      key={JSON.stringify(params)}
      loadOptions={loadOptions}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      placeholder={placeholder || t("common.select_options")}
      className={classNames}
      isSearchable
      isMulti={isMulti}
      // KHÔNG override `isLoading` bằng `apiLoading` nữa: `apiLoading` là cờ DÙNG CHUNG của cả hook
      // (true bất kể ĐANG tải cho từ khoá nào), trong khi thư viện tự tính đúng trạng thái loading CHO
      // RIÊNG từ khoá đang gõ (`asyncPaginateProps.isLoading`, theo dõi từng ô nhớ tạm theo inputValue).
      // Không truyền gì để thư viện tự quyết — chính xác hơn, đặc biệt lúc gõ nhanh nhiều ký tự liên tiếp.
      noOptionsMessage={() => t("common.no_result")}
      menuPortalTarget={document.body}
      value={displayValue}
      onChange={handleChange}
      styles={customStyles}
      menuPlacement="auto"
      menuPosition="fixed"
      additional={{ page: 1 }}
      debounceTimeout={300}
      cacheUniqs={[JSON.stringify(params), url]}
      closeMenuOnSelect={closeMenuOnSelectValue}
      isDisabled={disabled}
      defaultOptions={true}
      // Nếu đã có defaultOptions, tránh auto-fetch lần đầu để không bị gọi API 2 lần
      // Bật prefetch để không phải chờ khi người dùng mở dropdown.
    />
  );
};

export default SelectDropdownAsyncPaginate;
